import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { TenantsAPI } from "@/lib/sprint2-api";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  Copy,
  Check,
  Loader2,
  Power,
  PowerOff,
  Sparkles,
  Settings,
} from "lucide-react";
import { Link } from "react-router-dom";

const PLANS = ["trial", "starter", "pro", "enterprise"];

export default function Tenants() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data: tenants, isLoading } = useQuery({
    queryKey: ["tenants"],
    queryFn: () => TenantsAPI.list(),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, is_active }) => TenantsAPI.update(id, { is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast.success("Tenant updated");
    },
    onError: (e) => {
      console.error("Tenant mutation failed:", e);
      toast.error(formatApiErrorDetail(e));
    },
  });

  const isSuper = user?.roles?.includes("super_admin");

  return (
    <AppShell>
      <div className="p-8" data-testid="tenants-page">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
<h1 className="font-display font-extrabold tracking-tight text-4xl">
              Tenants
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Each tenant is an isolated educational organisation. Onboarding
              provisions both the tenant and its first <code className="font-mono text-xs px-1.5 py-0.5 bg-secondary rounded">tenant_admin</code> account in one shot.
            </p>
          </div>
          {isSuper && (
            <OnboardDialog open={open} setOpen={setOpen} onDone={() => queryClient.invalidateQueries({ queryKey: ["tenants"] })} />
          )}
        </div>

        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Modules</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && (tenants?.length || 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Building2 className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                    <div className="text-sm font-medium">No tenants yet</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {isSuper ? "Onboard your first institution to get started." : "Your account is not attached to any tenant."}
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {tenants?.map((t) => (
                <TableRow key={t.id} data-testid={`tenant-row-${t.slug}`}>
                  <TableCell>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{t.id.slice(-8)}</div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{t.slug}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="uppercase text-[10px] font-mono tracking-wider">
                      {t.plan}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(t.modules_enabled || []).slice(0, 3).map((m) => (
                        <Badge key={m} variant="secondary" className="text-[10px]">{m}</Badge>
                      ))}
                      {(t.modules_enabled || []).length > 3 && (
                        <Badge variant="secondary" className="text-[10px]">+{t.modules_enabled.length - 3}</Badge>
                      )}
                      {(t.modules_enabled || []).length === 0 && (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {t.is_active ? (
                      <Badge className="bg-[hsl(var(--success))/0.1] text-[hsl(var(--success))] border-[hsl(var(--success))/0.2] hover:bg-[hsl(var(--success))/0.15]">
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/tenants/${t.id}`)}
                      data-testid={`tenant-manage-${t.slug}`}
                    >
                      <Settings className="h-3.5 w-3.5 mr-1.5" /> Manage
                    </Button>
                    {isSuper && t.is_demo && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          if (!window.confirm(`Reset/seed demo data for "${t.name}"? Existing rows are kept, missing ones are filled in.`)) return;
                          try {
                            const out = await TenantsAPI.resetDemo(t.id);
                            toast.success(`Demo refreshed · +${out.students || 0} students · +${out.classes || 0} classes`);
                          } catch (e) {
                            console.error("Reset demo failed:", e);
                            toast.error(formatApiErrorDetail(e) || "Failed to reset demo");
                          }
                        }}
                        data-testid={`tenant-reset-demo-${t.slug}`}
                        title="Reset demo data"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {isSuper && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActive.mutate({ id: t.id, is_active: !t.is_active })}
                        data-testid={`tenant-toggle-${t.slug}`}
                      >
                        {t.is_active ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </AppShell>
  );
}

function OnboardDialog({ open, setOpen, onDone }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    // Step 1: Tenant identity
    name: "",
    slug: "",
    plan: "trial",
    tenant_type: "k12",
    country: "India",
    timezone: "Asia/Kolkata",
    currency: "INR",
    logo_url: "",
    primary_color: "",
    // Step 2: Institution + first AY
    inst_name: "",
    affiliation: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    phone: "",
    create_first_year: true,
    first_year_label: "AY 2026-27",
    // Step 3: Tenant admin
    admin_email: "",
    admin_name: "",
    admin_phone: "",
    admin_password: "",
    send_welcome_email: false,
    // Modules
    modules_enabled: ["admissions", "attendance", "fees", "notices", "homework", "library", "curriculum"],
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setForm({
      name: "", slug: "", plan: "trial", tenant_type: "k12", country: "India",
      timezone: "Asia/Kolkata", currency: "INR", logo_url: "", primary_color: "",
      inst_name: "", affiliation: "", address: "", city: "", state: "",
      pincode: "", phone: "", create_first_year: true, first_year_label: "AY 2026-27",
      admin_email: "", admin_name: "", admin_phone: "", admin_password: "",
      send_welcome_email: false,
      modules_enabled: ["admissions", "attendance", "fees", "notices", "homework", "library", "curriculum"],
    });
    setStep(1);
    setResult(null);
    setError("");
  };

  const toggleModule = (m) => {
    const set = new Set(form.modules_enabled);
    if (set.has(m)) set.delete(m); else set.add(m);
    setForm({ ...form, modules_enabled: Array.from(set) });
  };

  const validStep1 = form.name.trim().length >= 2;
  const validStep3 = form.admin_email.trim() && form.admin_name.trim().length >= 2;

  const submit = async () => {
    setError("");
    setLoading(true);
    try {
      const out = await TenantsAPI.onboard({
        tenant: {
          name: form.name,
          slug: form.slug || undefined,
          plan: form.plan,
          tenant_type: form.tenant_type,
          country: form.country || undefined,
          timezone: form.timezone || undefined,
          currency: form.currency || undefined,
          logo_url: form.logo_url || undefined,
          primary_color: form.primary_color || undefined,
          modules_enabled: form.modules_enabled,
        },
        admin_email: form.admin_email,
        admin_name: form.admin_name,
        admin_phone: form.admin_phone || undefined,
        admin_password: form.admin_password || undefined,
        send_welcome_email: form.send_welcome_email,
        institution: {
          name: form.inst_name || undefined,
          affiliation: form.affiliation || undefined,
          address: form.address || undefined,
          city: form.city || undefined,
          state: form.state || undefined,
          pincode: form.pincode || undefined,
          phone: form.phone || undefined,
          create_first_year: form.create_first_year,
          first_year_label: form.first_year_label || undefined,
        },
      });
      setResult(out);
      toast.success(`Tenant "${out.tenant.name}" onboarded`);
      onDone?.();
    } catch (e) {
      console.error("Onboard tenant failed:", e);
      setError(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const MODULES = ["admissions", "attendance", "fees", "notices", "homework", "library", "curriculum", "transport", "hostel", "gradebook", "timetable", "hr"];

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="onboard-tenant-button">
          <Plus className="h-4 w-4 mr-1.5" /> Onboard tenant
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold tracking-tight text-2xl">
            {result ? "Tenant onboarded" : `Onboard new tenant · step ${step} of 3`}
          </DialogTitle>
          <DialogDescription>
            {result
              ? "Save these credentials before closing this dialog."
              : "Creates a tenant + its first institution + a tenant_admin user."}
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4" data-testid="onboard-form">
            {/* Step indicator */}
            <div className="flex gap-2">
              {[1, 2, 3].map((n) => (
                <div key={n} className={`flex-1 h-1.5 rounded-full ${step >= n ? "bg-primary" : "bg-secondary"}`} />
              ))}
            </div>

            {/* STEP 1 — Tenant identity */}
            {step === 1 && (
              <div className="space-y-3" data-testid="onboard-step-1">
                <Field label="Tenant name" required>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Westbridge Public School" data-testid="onboard-tenant-name" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Slug (optional)">
                    <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated" data-testid="onboard-tenant-slug" className="font-mono text-sm" />
                  </Field>
                  <Field label="Plan">
                    <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                      <SelectTrigger data-testid="onboard-tenant-plan"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PLANS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Tenant type">
                    <Select value={form.tenant_type} onValueChange={(v) => setForm({ ...form, tenant_type: v })}>
                      <SelectTrigger data-testid="onboard-tenant-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="k12">K-12 School</SelectItem>
                        <SelectItem value="higher_ed">Higher Education</SelectItem>
                        <SelectItem value="coaching">Coaching / Tuition</SelectItem>
                        <SelectItem value="mixed">Mixed</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Country">
                    <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="India" data-testid="onboard-tenant-country" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Timezone">
                    <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} placeholder="Asia/Kolkata" data-testid="onboard-tenant-tz" />
                  </Field>
                  <Field label="Currency">
                    <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} placeholder="INR" data-testid="onboard-tenant-currency" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Logo URL (optional)">
                    <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…" data-testid="onboard-tenant-logo" />
                  </Field>
                  <Field label="Primary colour (optional)">
                    <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} placeholder="#0033CC" data-testid="onboard-tenant-color" />
                  </Field>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mt-2 mb-2">Enabled modules</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="onboard-modules">
                    {MODULES.map((m) => (
                      <button key={m} type="button" onClick={() => toggleModule(m)} className={`text-[11px] px-2 py-1 rounded border ${form.modules_enabled.includes(m) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`} data-testid={`onboard-module-${m}`}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2 — Institution */}
            {step === 2 && (
              <div className="space-y-3" data-testid="onboard-step-2">
                <Field label="Institution name">
                  <Input value={form.inst_name} onChange={(e) => setForm({ ...form, inst_name: e.target.value })} placeholder={form.name || "Defaults to tenant name"} data-testid="onboard-inst-name" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Affiliation / board">
                    <Select value={form.affiliation || "none"} onValueChange={(v) => setForm({ ...form, affiliation: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="onboard-inst-affiliation"><SelectValue placeholder="Select board" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="CBSE">CBSE</SelectItem>
                        <SelectItem value="ICSE">ICSE</SelectItem>
                        <SelectItem value="State">State Board</SelectItem>
                        <SelectItem value="IB">IB</SelectItem>
                        <SelectItem value="Cambridge">Cambridge</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Contact phone">
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 …" data-testid="onboard-inst-phone" />
                  </Field>
                </div>
                <Field label="Address">
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, building" data-testid="onboard-inst-address" />
                </Field>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="City">
                    <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="onboard-inst-city" />
                  </Field>
                  <Field label="State">
                    <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="onboard-inst-state" />
                  </Field>
                  <Field label="Pincode">
                    <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} data-testid="onboard-inst-pincode" />
                  </Field>
                </div>
                <label className="flex items-start gap-2 text-sm cursor-pointer pt-2">
                  <input type="checkbox" checked={form.create_first_year} onChange={(e) => setForm({ ...form, create_first_year: e.target.checked })} data-testid="onboard-create-ay" />
                  <div className="min-w-0">
                    <div className="font-medium">Create first academic year</div>
                    <div className="text-xs text-muted-foreground">
                      Auto-creates an AY so the admin can start setting up classes immediately.
                    </div>
                  </div>
                </label>
                {form.create_first_year && (
                  <Field label="Academic year label">
                    <Input value={form.first_year_label} onChange={(e) => setForm({ ...form, first_year_label: e.target.value })} placeholder="AY 2026-27" data-testid="onboard-ay-label" />
                  </Field>
                )}
              </div>
            )}

            {/* STEP 3 — Tenant admin account */}
            {step === 3 && (
              <div className="space-y-3" data-testid="onboard-step-3">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-1">
                  Tenant admin account (role: tenant_admin)
                </div>
                <Field label="Admin email" required>
                  <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} placeholder="admin@westbridge.edu" data-testid="onboard-admin-email" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Admin name" required>
                    <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} placeholder="Aarti Mehta" data-testid="onboard-admin-name" />
                  </Field>
                  <Field label="Admin phone (optional)">
                    <Input value={form.admin_phone} onChange={(e) => setForm({ ...form, admin_phone: e.target.value })} placeholder="+91 …" data-testid="onboard-admin-phone" />
                  </Field>
                </div>
                <Field label="Password (optional)">
                  <Input value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} placeholder="auto-generated" type="text" data-testid="onboard-admin-password" className="font-mono text-sm" />
                </Field>
                <label className="flex items-start gap-2 text-sm cursor-pointer pt-2">
                  <input type="checkbox" checked={form.send_welcome_email} onChange={(e) => setForm({ ...form, send_welcome_email: e.target.checked })} data-testid="onboard-send-welcome" />
                  <div className="min-w-0">
                    <div className="font-medium">Send welcome email</div>
                    <div className="text-xs text-muted-foreground">
                      MOCKED — logged server-side; activates once SendGrid keys land.
                    </div>
                  </div>
                </label>
              </div>
            )}

            {error && (
              <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2" data-testid="onboard-error">
                {error}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              {step > 1 && (
                <Button type="button" variant="ghost" onClick={() => setStep(step - 1)} data-testid="onboard-back">
                  Back
                </Button>
              )}
              {step < 3 ? (
                <Button type="button" onClick={() => setStep(step + 1)} disabled={step === 1 && !validStep1} data-testid="onboard-next">
                  Next
                </Button>
              ) : (
                <Button type="button" onClick={submit} disabled={loading || !validStep3} data-testid="onboard-submit">
                  {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Onboard
                </Button>
              )}
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4" data-testid="onboard-success">
            <div className="border border-[hsl(var(--success))/0.3] bg-[hsl(var(--success))/0.05] rounded-md p-4">
              <div className="font-semibold text-sm flex items-center gap-2">
                <Check className="h-4 w-4 text-[hsl(var(--success))]" /> Tenant created
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Slug: <span className="font-mono text-foreground">{result.tenant.slug}</span>
              </div>
            </div>
            <div className="space-y-3">
              <Row label="Login email">
                <code className="font-mono text-xs" data-testid="onboard-result-email">{result.admin.email}</code>
              </Row>
              {result.temporary_password && (
                <Row label="Temporary password">
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-xs bg-secondary px-2 py-1 rounded border" data-testid="onboard-result-temp-password">
                      {result.temporary_password}
                    </code>
                    <Button size="sm" variant="outline" type="button" onClick={() => copy(result.temporary_password)}>
                      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    </Button>
                  </div>
                </Row>
              )}
            </div>
            <DialogFooter>
              <Button onClick={reset} variant="outline" data-testid="onboard-add-another">Onboard another</Button>
              <Button onClick={() => setOpen(false)} data-testid="onboard-done">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider">
        {label}{required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between border-b border-border pb-2">
      <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div>{children}</div>
    </div>
  );
}
